import React, {useRef, useState} from "react";
import {uploadMedia} from "../api/media";
import {mediaUrl} from "./media";

/**
 * Photos for a listing or project: upload (pick or drop), remove, and reorder; the first photo is the cover.
 * `value` is the list of stored paths/URLs, `onChange` gets the new list.
 */
const PhotoManager = ({value = [], onChange, max = 20, hint, disabled}) => {
    const input = useRef(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [dragging, setDragging] = useState(false);

    const add = async (files) => {
        const list = Array.from(files || []).filter((f) => f.type.startsWith("image/"));
        if (!list.length) return;
        const room = max - value.length;
        if (room <= 0) {
            setError(`You can add up to ${max} photos.`);
            return;
        }
        setBusy(true);
        setError("");
        try {
            const uploaded = await uploadMedia(list.slice(0, room), "image");
            onChange([...value, ...uploaded.map((u) => u.path)]);
            if (list.length > room) setError(`Only the first ${room} were added (limit ${max}).`);
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(false);
            if (input.current) input.current.value = "";
        }
    };

    const move = (from, to) => {
        const next = [...value];
        const [item] = next.splice(from, 1);
        next.splice(to, 0, item);
        onChange(next);
    };

    return (
        <div className="brk-photos">
            <div
                className={`brk-dropzone${dragging ? " brk-dropzone--over" : ""}${disabled ? " brk-dropzone--off" : ""}`}
                onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={(e) => { e.preventDefault(); setDragging(false); if (!disabled) add(e.dataTransfer.files); }}
            >
                <p>{busy ? "Uploading…" : "Drag photos here, or"}</p>
                <button type="button" className="brk-btn brk-btn--ghost brk-btn--sm" disabled={busy || disabled}
                        onClick={() => input.current?.click()}>
                    Choose photos
                </button>
                <input ref={input} type="file" accept="image/jpeg,image/png,image/webp" multiple hidden
                       onChange={(e) => add(e.target.files)}/>
                <span className="brk-hint">{hint || "JPG, PNG or WebP, up to 15 MB each. The first photo is the cover."}</span>
            </div>
            {error && <p className="brk-field__error" role="alert">{error}</p>}
            {value.length > 0 && (
                <ul className="brk-photo-grid">
                    {value.map((path, i) => (
                        <li key={path} className="brk-photo">
                            <img src={mediaUrl(path)} alt={i === 0 ? "Cover" : `Gallery ${i + 1}`} loading="lazy"/>
                            {i === 0 && <span className="brk-photo__cover">Cover</span>}
                            <div className="brk-photo__actions">
                                {i > 0 && (
                                    <button type="button" title="Make cover" aria-label={`Make photo ${i + 1} the cover`}
                                            onClick={() => move(i, 0)}>★</button>
                                )}
                                {i > 0 && (
                                    <button type="button" title="Move left" aria-label={`Move photo ${i + 1} left`}
                                            onClick={() => move(i, i - 1)}>←</button>
                                )}
                                <button type="button" title="Remove" aria-label={`Remove photo ${i + 1}`}
                                        onClick={() => onChange(value.filter((_, j) => j !== i))}>✕</button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default PhotoManager;
